import { useCloseDeletedSelection } from "../../../components/common/useCloseDeletedSelection";
import CollectionItemActions from "./CollectionItemActions";
import { nextCollectionNumber, useCollectionItems } from "./collectionItems";
import { useEffect, useRef, useState } from "react";
import { Accordion, Form, AccordionButton, AccordionCollapse, AccordionHeader, AccordionItem, Alert, Button, Col, FormCheck, FormControl, FormGroup, FormLabel, InputGroup, Row } from "react-bootstrap";
import { IAccordionItem, IFieldEditor } from "../../../../models/Interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import "@fortawesome/free-regular-svg-icons";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { createFormValidation } from "../../../../helpers/formValidationHelper";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import HTMLEditor from "../../../components/common/HTMLEditor";
import { accordionItemFrame } from "../../../components/common/editorSiteFrame";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { Core, formValidation, notEmpty, callback } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import { Message } from "@form-validation/plugin-message";


const AccordionEditor = (props: IFieldEditor) => {

  const [items, setItems] = useCollectionItems<IAccordionItem>(props);
  const [selectedItem, setSelectedItem] = useState<IAccordionItem | null>(null);
  const [editorOpen, setEditorOpen] = useState<boolean>(false);

  const fvRef = useRef<Core | null>(null);
  const [editorSession, setEditorSession] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);


  const addItem = () : void => {
    const item: IAccordionItem = {
      Id: nextCollectionNumber(items),
      BackgroundColour: undefined,
      Content: "",
      Expanded: false,
      IconClass: "",
      ItemClass: "",
      ItemContentClass: "",
      TextColour: undefined,
      Title: ""
    };

    setSelectedItem(item);
    openCloseEditor(true);
  };

  const setValidation = () : void => {
    if (formRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Title: {
            validators: {
              callback: {
                callback: (field: HTMLTextAreaElement) => {
                  const validator = { valid: false, message: "A title is required for the accordion item" };
                  
                  if (field?.value) {
                    validator.valid = true;
                    validator.message = "";
                  }
                  return validator;
                }
              }
            }
          },
          Content: {
            validators: {
              callback: {
                callback: (field: HTMLTextAreaElement) => {
                  const validator = { valid: false, message: "Accordion content is required" };
                  
                  if (field?.value) {
                    validator.valid = true;
                    validator.message = "";
                  }
                  return validator;
                }
              }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          submitButton: new SubmitButton(),
        }
      });
    }
  };

  const editItem = (item: any) => {
    if (item && item != null) {
      setSelectedItem(item);
      openCloseEditor(true);
    }
  };

  const itemPropertyChange = (event: React.ChangeEvent<HTMLInputElement>) : void => {
    const {name, value} = event.target;

    if ((name === "Title" || name === "Content") && fvRef.current) {
      fvRef.current.revalidateField(name);
    }

    setProperty(name, value);
  };

  const setProperty = (name: string, value: string | null | undefined) : void => {
    if (name) {
      setSelectedItem(prev => ({
        ...prev as IAccordionItem,
        [name]: value
      }));
    }
  };

  const onItemSave = async (item: IAccordionItem) : Promise<void> => {
    if (fvRef.current) {
      const res = await fvRef.current.validate();

      if (res === "Valid") {
        if (item) {
          const existing = items.find(itm => itm.Id === item.Id);

          if (existing) {
            const updated = items.map(existing => existing.Id === item.Id ? item : existing);
            setItems(updated);
          } else {
            setItems(prev => [
              ...prev,
              item
            ]);
          }

          openCloseEditor(false);
        }
      }
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => openCloseEditor(false));

  const onItemDelete = (item: IAccordionItem) : void => {
    if (item) {
      const updated = items.filter(itm => itm.Id !== item.Id);

      if (updated) {
        setItems(updated);
        closeDeletedSelection(item.Id);
      }
    }
  };


  const openCloseEditor = (open: boolean) : void => {
    if (open) setEditorSession(session => session + 1);
    if (props.onEditorOpen) {
      props.onEditorOpen(open);
    }

    if (!open) {
      setEditorOpen(false);
    } else {
      setEditorOpen(true);
    }
  };




  useEffect(() => {
    if (!editorOpen) return;
    setValidation();
    return () => {
      fvRef.current?.destroy();
      fvRef.current = null;
    };
  }, [editorOpen]);

  return (
    <>
      {!editorOpen && (
        <Row>
          <Col xs={12} className="mt-3">
            <Button variant="outline-secondary" onClick={addItem}>
              <FontAwesomeIcon icon={faPlus} />&nbsp;Add item
            </Button>
          </Col>
        </Row>
      )}

      {!editorOpen && (
        <div className="mt-3">
          <Accordion key={`accordion-${props.property.Id}`}>
            {items.map((item: IAccordionItem, index) => (
              <Row className="m-0 p-0" key={`accordion-item-${index}`}>
                <Col xs={11} className="m-0 p-0 pe-2 pb-2">
                  <Accordion.Item eventKey={index.toString()} key={item.Id}>
                    <Accordion.Header>
                      <span className={`${item.IconClass} pe-2`} ></span>
                      <span dangerouslySetInnerHTML={{__html: item.Title ?? ""}}></span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <span dangerouslySetInnerHTML={{__html: item.Content ?? "" }}></span>
                    </Accordion.Body>
                  </Accordion.Item>
                </Col>
                <Col xs={1} className="m-0 p-0">
                  <div className="accordion-editor-buttons">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      title="edit item"
                      className="btn-nomin me-1"
                      onClick={() => editItem(item)}
                    >
                      <FontAwesomeIcon icon={faEdit}></FontAwesomeIcon>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      title="delete item"
                      className="btn-nomin"
                      onClick={() => onItemDelete(item)}
                    >
                      <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                    </Button>
                    <CollectionItemActions items={items} index={index} onChange={setItems} />
                  </div>               
                </Col>
              </Row>
            ))}
          </Accordion>
        </div>
      )}
      
      { (items.length <= 0 && !editorOpen) && (
        <Alert variant="info" className="p-3 mt-3 text-center">
          <span>No accordion items configured</span>
        </Alert>        
      )}

      { selectedItem !== null && (
        <div className="accordion-item-editor" hidden={!editorOpen} data-nested-editor-open={editorOpen ? "true" : undefined}>
          <Form id="AccordionItemsForm" name="AccordionItemsForm" ref={formRef}>
            <FormGroup>
              <Row>
                <Col xs={12} className="mt-3">
                  <FormLabel>Item Title (Collapsed)</FormLabel>
                  <HTMLEditor documentKey={editorSession} height={props.editorHeight}
                    id="Title"
                    name="Title" 
                    onInputChange={itemPropertyChange}
                    siteFrame={accordionItemFrame(props.siteFrame, "title")}
                    value={selectedItem.Title ?? ""} />
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="mt-3">
                  <FormLabel>Item Content (Expanded)</FormLabel>
                  <HTMLEditor documentKey={editorSession} height={props.editorHeight}
                    id="Content"
                    name="Content"
                    onInputChange={itemPropertyChange}
                    siteFrame={accordionItemFrame(props.siteFrame, "content")}
                    value={selectedItem.Content ?? ""} />
                </Col>
              </Row>
              <Row>
                <Col xs={6} className="mt-3">
                  <FormLabel>Item Icon</FormLabel>
                  <FormControl 
                    type="text" 
                    name="IconClass"
                    value={selectedItem.IconClass ?? ""} 
                    onChange={itemPropertyChange}
                    placeholder="icon class e.g. fas fa-question..." />
                </Col>
                <Col xs={6} className="mt-3">
                  <FormLabel>Item Class</FormLabel>
                  <FormControl 
                    type="text" 
                    name="ItemClass"
                    onChange={itemPropertyChange}
                    value={selectedItem.ItemClass ?? ""} placeholder="collaped item class..." />
                </Col>
              </Row>
              <Row>
                <Col xs={6} className="mt-3">
                  <FormLabel>Content Class</FormLabel>
                  <FormControl 
                    type="text" 
                    name="ItemContentClass"
                    onChange={itemPropertyChange}
                    value={selectedItem.ItemContentClass ?? ""} 
                    placeholder="expended item class..." />
                </Col>
                <Col xs={6} className="mt-3">
                  <FormLabel htmlFor="DefaultExpanded">Show Initial Expanded</FormLabel>
                  <FormCheckInput
                    type="checkbox" 
                    name="Expanded"
                    className="btn-nomin form-check" 
                    onChange={itemPropertyChange}
                    checked={selectedItem.Expanded} 
                    id="DefaultExpanded" />
                </Col>
              </Row>
              <Row>
                <Col xs={6} className="mt-3">
                  <FormLabel>Text Colour</FormLabel>
                  <InputGroup>
                    <FormControl 
                      type="color"
                      name="TextColour"
                      onChange={itemPropertyChange}
                      value={selectedItem.TextColour ?? ""} />
                    <InputGroupText>
                      <FontAwesomeIcon 
                        icon={faTimes} 
                        className="text-secondary" 
                        style={{cursor: "pointer"}}
                        onClick={() => setProperty("TextColour", null)} 
                        title="clear colour" />
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={6} className="mt-3">
                <FormLabel>Background Colour</FormLabel>
                  <InputGroup>
                    <FormControl 
                      type="color"
                      name="BackgroundColour"
                      onChange={itemPropertyChange}
                      value={selectedItem.BackgroundColour ?? ""} />
                    <InputGroupText>
                      <FontAwesomeIcon 
                        icon={faTimes} 
                        className="text-secondary" 
                        style={{cursor: "pointer"}} 
                        onClick={() => setProperty("BackgroundColour", null)}
                        title="clear colour" />
                    </InputGroupText>
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12} className="mt-3 text-end">
                  <hr className="mt-2 mb-2" />
                  <Button variant="outline-success me-2" title="save item" onClick={() => onItemSave(selectedItem)}>
                    <FontAwesomeIcon icon={faPlus} />
                    &nbsp;Save
                  </Button>
                  <Button variant="outline-primary" title="close item edit" onClick={() => openCloseEditor(false)}>
                    Close
                  </Button>
                </Col>
              </Row>
            </FormGroup>
          </Form>
        </div>
      )}
    </>
  );
}

export default AccordionEditor;
